import React, { useState, useEffect } from 'react';
import { GalleryEvent, GalleryImage } from '../../types/Gallery';
import { apiClient } from '../../config/api';
import { Upload, X, Eye, Edit2, Trash2, Camera } from 'lucide-react';

interface GalleryImageManagerProps {
  galleryEvent: GalleryEvent;
  isOpen: boolean;
  onClose: () => void;
  onEventUpdate: (updatedEvent: GalleryEvent) => void;
}

const GalleryImageManager: React.FC<GalleryImageManagerProps> = ({
  galleryEvent,
  isOpen,
  onClose,
  onEventUpdate
}) => {
  const [images, setImages] = useState<GalleryImage[]>(galleryEvent.images || []);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [isEditingImage, setIsEditingImage] = useState(false);
  const [imageFormData, setImageFormData] = useState({
    caption: '',
    photographer: ''
  });
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Helper function to get the correct image URL (S3 or base64)
  const getImageDataUrl = (image: GalleryImage): string => {
    console.log('GalleryImageManager - Processing image:', {
      id: image.id,
      filename: image.filename,
      hasS3Url: !!image.s3Url,
      s3Url: image.s3Url,
      hasBase64Data: !!image.base64Data,
      base64DataLength: image.base64Data ? image.base64Data.length : 0
    });

    // Prefer S3 URL if available (for S3-stored images)
    if (image.s3Url && image.s3Url.trim() !== '') {
      console.log('GalleryImageManager - Using S3 URL:', image.s3Url);
      return image.s3Url;
    }
    // Fallback to base64 data for backward compatibility (only if valid base64 data exists)
    if (image.base64Data && image.base64Data.trim() !== '' && image.base64Data !== 'undefined') {
      const dataUrl = `data:${image.contentType};base64,${image.base64Data}`;
      console.log('GalleryImageManager - Using base64 data URL (first 100 chars):', dataUrl.substring(0, 100));
      return dataUrl;
    }
    // If neither is available, return empty string (will trigger onError handler)
    console.log('GalleryImageManager - No valid image data found, returning empty string');
    return '';
  };

  useEffect(() => {
    console.log('GalleryImageManager - Gallery event data:', {
      eventId: galleryEvent.id,
      eventTitle: galleryEvent.title,
      imagesCount: galleryEvent.images?.length || 0,
      images: galleryEvent.images
    });
    setImages(galleryEvent.images || []);
  }, [galleryEvent]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);

    try {
      for (const file of Array.from(files)) {
        // Convert file to base64
        const base64Data = await fileToBase64(file);

        const imageData = {
          filename: file.name,
          contentType: file.type,
          base64Data: base64Data.split(',')[1], // Remove data:image/... prefix
          size: file.size,
          caption: '',
          photographer: ''
        };

        // Upload image to the gallery event
        const response = await apiClient.post(`/api/admin/gallery/events/${galleryEvent.id}/images`, imageData);

        if (response) {
          // Update local state with the updated gallery event
          onEventUpdate(response);
          setImages(response.images || []);
        }
      }
    } catch (error) {
      console.error('Failed to upload images:', error);
      alert('Failed to upload images. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const handleDeleteImage = async (imageId: string) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await apiClient.delete(`/api/admin/gallery/events/${galleryEvent.id}/images/${imageId}`);

      // Update local state
      const updatedImages = images.filter(img => img.id !== imageId);
      setImages(updatedImages);

      // Update parent component
      const updatedEvent = { ...galleryEvent, images: updatedImages };
      onEventUpdate(updatedEvent);
    } catch (error) {
      console.error('Failed to delete image:', error);
      alert('Failed to delete image. Please try again.');
    }
  };

  const handleEditImage = (image: GalleryImage) => {
    setSelectedImage(image);
    setImageFormData({
      caption: image.caption || '',
      photographer: image.photographer || ''
    });
    setIsEditingImage(true);
  };

  const handleUpdateImage = async () => {
    if (!selectedImage) return;

    try {
      const updatedImageData = {
        ...selectedImage,
        caption: imageFormData.caption,
        photographer: imageFormData.photographer
      };

      const response = await apiClient.put(
        `/api/admin/gallery/events/${galleryEvent.id}/images/${selectedImage.id}`,
        updatedImageData
      );

      if (response) {
        // Update local state
        const updatedImages = images.map(img =>
          img.id === selectedImage.id ? response : img
        );
        setImages(updatedImages);

        // Update parent component
        const updatedEvent = { ...galleryEvent, images: updatedImages };
        onEventUpdate(updatedEvent);
      }

      setIsEditingImage(false);
      setSelectedImage(null);
    } catch (error) {
      console.error('Failed to update image:', error);
      alert('Failed to update image. Please try again.');
    }
  };

  const handlePreviewImage = (image: GalleryImage) => {
    // Use the same logic as getImageDataUrl to handle both S3 and base64 images
    const imageUrl = getImageDataUrl(image);
    setPreviewImage(imageUrl);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-11/12 md:w-5/6 lg:w-4/5 shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-medium text-gray-900">Manage Images</h3>
            <p className="text-sm text-gray-600">{galleryEvent.title}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Upload Section */}
        <div className="mb-6">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-2">
              <label htmlFor="file-upload" className="cursor-pointer">
                <span className="mt-2 block text-sm font-medium text-gray-900">
                  Upload new images
                </span>
                <span className="mt-1 block text-sm text-gray-500">
                  PNG, JPG, GIF up to 10MB each
                </span>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  multiple
                  accept="image/*"
                  className="sr-only"
                  onChange={handleFileUpload}
                  disabled={isUploading}
                />
              </label>
            </div>
            {isUploading && (
              <div className="mt-2">
                <div className="bg-blue-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full animate-pulse"></div>
                </div>
                <p className="text-sm text-gray-600 mt-1">Uploading...</p>
              </div>
            )}
          </div>
        </div>

        {/* Images Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image) => (
            <div key={image.id} className="relative group">
              <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden">
                <img
                  src={getImageDataUrl(image)}
                  alt={image.caption || image.filename}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    console.error('GalleryImageManager - Image failed to load:', {
                      src: getImageDataUrl(image),
                      filename: image.filename,
                      error: e
                    });
                    // Show error state
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                  onLoad={() => {
                    console.log('GalleryImageManager - Image loaded successfully:', {
                      src: getImageDataUrl(image),
                      filename: image.filename
                    });
                  }}
                />
              </div>

              {/* Image overlay with actions */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePreviewImage(image)}
                    className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100"
                    title="Preview"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEditImage(image)}
                    className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100"
                    title="Edit"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteImage(image.id)}
                    className="p-2 bg-white rounded-full text-red-600 hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Image info */}
              <div className="mt-2">
                <p className="text-xs text-gray-600 truncate">{image.filename}</p>
                {image.caption && (
                  <p className="text-xs text-gray-500 truncate">{image.caption}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {images.length === 0 && (
          <div className="text-center py-12">
            <Camera className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No images</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by uploading your first image.</p>
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60">
          <div className="max-w-4xl max-h-full p-4">
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-full object-contain"
            />
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300"
            >
              <X className="h-8 w-8" />
            </button>
          </div>
        </div>
      )}

      {/* Image Edit Modal */}
      {isEditingImage && selectedImage && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h4 className="text-lg font-medium mb-4">Edit Image Details</h4>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Caption</label>
                <input
                  type="text"
                  value={imageFormData.caption}
                  onChange={(e) => setImageFormData(prev => ({ ...prev, caption: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Enter image caption..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Photographer</label>
                <input
                  type="text"
                  value={imageFormData.photographer}
                  onChange={(e) => setImageFormData(prev => ({ ...prev, photographer: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Enter photographer name..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setIsEditingImage(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateImage}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryImageManager;
